import React, { useState, useEffect } from "react";
import {
  faPenToSquare,
  faTrash,
  faUserGear,
  faSearch,
  faRotateRight,
} from "@fortawesome/free-solid-svg-icons";
import { Tooltip, Pagination, Chip, IconButton } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { CheckRole, deleteUser, getUsers } from "../../Services/AuthService";
import Content from "../../Components/Core/Content";
import { Button } from "@mui/material";
import Loader from "../../Components/Core/Loader";
import { useLocation, useNavigate } from "react-router-dom";
import Notification from "../../Components/Core/Notification";
import ConfirmModal from "../../Components/Modals/ConfirmModal";
import EditModal from "../../Components/Modals/EditModal";
import FormInput from "../../Components/Core/FormInput";

const UserIndex = () => {
  const [users, setUsers] = useState([]);
  const [name, setName] = useState("");
  const [mobileNumber, setMobileNumber] = useState("");
  const [nationalCode, setNationalCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(50);
  const [totalItems, setTotalItems] = useState(0);
  const [hasActiveFilters, setHasActiveFilters] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();

  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const fetchUsers = async (filter = {}, page = 1) => {
    try {
      setLoading(true);
      const params = {
        ...filter,
        page: page,
        pageSize: itemsPerPage,
      };
      const response = await getUsers(params);
      const data = await response.json();

      setUsers(data.items || data); // Handle both paginated and non-paginated responses
      setTotalItems(data.totalCount || data.length);
      setCurrentPage(page);
    } catch (error) {
      console.error(error);
      setSnackbarMessage("خطا در دریافت اطلاعات کاربران");
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    const filter = {};
    if (name.trim()) filter.name = name.trim();
    if (nationalCode.trim()) filter.nationalCode = nationalCode.trim();
    if (mobileNumber.trim()) filter.mobileNumber = mobileNumber.trim();

    setHasActiveFilters(
      name.trim() || nationalCode.trim() || mobileNumber.trim()
    );
    fetchUsers(filter, 1);
  };

  const handleReset = () => {
    setName("");
    setMobileNumber("");
    setNationalCode("");
    setHasActiveFilters(false);
    fetchUsers({}, 1);
  };

  const handlePageChange = (event, value) => {
    const filter = {};
    if (name.trim()) filter.name = name.trim();
    if (nationalCode.trim()) filter.nationalCode = nationalCode.trim();
    if (mobileNumber.trim()) filter.mobileNumber = mobileNumber.trim();

    fetchUsers(filter, value);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const deleteUserHandler = async () => {
    setOpenDialog(false);
    try {
      setLoading(true);
      await deleteUser(selectedUserId);
      setSnackbarMessage("کاربر با موفقیت حذف شد");
      setSnackbarSeverity("success");
      setOpenSnackbar(true);

      // Refresh current page
      const filter = {};
      if (name.trim()) filter.name = name.trim();
      if (nationalCode.trim()) filter.nationalCode = nationalCode.trim();
      if (mobileNumber.trim()) filter.mobileNumber = mobileNumber.trim();
      fetchUsers(filter, currentPage);
    } catch (error) {
      setSnackbarMessage(error.message || "خطا در حذف کاربر");
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
    } finally {
      setLoading(false);
    }
  };

  const totalPages = Math.ceil(totalItems / itemsPerPage);

  useEffect(() => {
    document.title = "مدیریت کاربران";
    fetchUsers({}, 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Content>
      <div className="container color-blue mb-5">
        {/* Header */}
        <h2 className="mb-4 row mt-5 highlight-box highlight-secondary fw-bold color-blue">
          <span>مدیریت کاربران</span>
        </h2>
        {/* Filters Card */}
        <div className="row mt-4">
          <div className="col-12">
            <div className="card border-0 shadow-sm">
              <div className="card-body p-4">
                <h5 className="fw-bold color-blue mb-4 iran-sans d-flex align-items-center gap-2">
                  <FontAwesomeIcon icon={faSearch} size="sm" />
                  جستجو و فیلتر
                </h5>

                <div className="row g-3">
                  <div className="col-md-3 col-12">
                    <FormInput
                      className="form-blue"
                      name="نام یا نام خانوادگی"
                      onChange={(e) => setName(e.target.value)}
                      value={name}
                      for="userName"
                      placeHolder="نام کاربر را وارد کنید"
                    />
                  </div>

                  <div className="col-md-3 col-12">
                    <FormInput
                      className="form-blue"
                      name="شماره موبایل"
                      onChange={(e) => setMobileNumber(e.target.value)}
                      value={mobileNumber}
                      for="mobileNumber"
                      placeHolder="09123456789"
                      type="tel"
                      dir="ltr"
                    />
                  </div>

                  <div className="col-md-3 col-12">
                    <FormInput
                      className="form-blue"
                      name="کد ملی"
                      onChange={(e) => setNationalCode(e.target.value)}
                      value={nationalCode}
                      for="nationalCode"
                      placeHolder="کد ملی 10 رقمی"
                      type="text"
                      dir="ltr"
                    />
                  </div>

                  <div className="col-md-3 col-12 d-flex align-items-end gap-2">
                    <Button
                      variant="contained"
                      className="btn-blue flex-grow-1"
                      onClick={handleSearch}
                      style={{ height: "37px", marginTop: "32px" }}
                    >
                      <FontAwesomeIcon icon={faSearch} className="me-2" />
                      <span>جستجو</span>
                    </Button>

                    {hasActiveFilters && (
                      <Tooltip title="پاک کردن فیلترها" arrow>
                        <IconButton
                          onClick={handleReset}
                          style={{
                            height: "37px",
                            width: "37px",
                            marginTop: "32px",
                            background: "#f8f9fa",
                            border: "1px solid #e9ecef",
                          }}
                        >
                          <FontAwesomeIcon
                            icon={faRotateRight}
                            size="sm"
                            style={{ color: "#053251" }}
                          />
                        </IconButton>
                      </Tooltip>
                    )}
                  </div>
                </div>

                {hasActiveFilters && (
                  <div className="mt-3 d-flex align-items-center gap-2 flex-wrap">
                    <span className="iran-sans" style={{ fontSize: "14px" }}>
                      فیلترهای فعال:
                    </span>
                    {name && (
                      <Chip
                        label={`نام: ${name}`}
                        onDelete={() => setName("")}
                        size="small"
                        sx={{
                          backgroundColor: "#e8f5e9",
                          color: "#053251",
                          fontFamily: "iran-sans",
                        }}
                      />
                    )}
                    {mobileNumber && (
                      <Chip
                        label={`موبایل: ${mobileNumber}`}
                        onDelete={() => setMobileNumber("")}
                        size="small"
                        sx={{
                          backgroundColor: "#e8f5e9",
                          color: "#053251",
                          fontFamily: "iran-sans",
                        }}
                      />
                    )}
                    {nationalCode && (
                      <Chip
                        label={`کد ملی: ${nationalCode}`}
                        onDelete={() => setNationalCode("")}
                        size="small"
                        sx={{
                          backgroundColor: "#e8f5e9",
                          color: "#053251",
                          fontFamily: "iran-sans",
                        }}
                      />
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Results Info */}
        {!loading && (
          <div className="row mt-3">
            <div className="col-12">
              <div className="d-flex justify-content-between align-items-center">
                <span
                  className="iran-sans"
                  style={{ fontSize: "14px", color: "#6c757d" }}
                >
                  تعداد کل: {totalItems} کاربر
                </span>
                <span
                  className="iran-sans"
                  style={{ fontSize: "14px", color: "#6c757d" }}
                >
                  صفحه {currentPage} از {totalPages || 1}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Users Table */}
        {loading ? (
          <Loader />
        ) : users.length === 0 ? (
          <div className="row mt-4">
            <div className="col-12">
              <div
                className="card border-0 shadow-sm text-center py-5"
                style={{
                  background:
                    "linear-gradient(135deg, rgba(124, 200, 144, 0.05) 0%, rgba(5, 50, 81, 0.02) 100%)",
                }}
              >
                <div className="card-body">
                  <div style={{ fontSize: "48px", marginBottom: "16px" }}>
                    👤
                  </div>
                  <h5 className="iran-sans fw-bold color-blue">
                    کاربری یافت نشد
                  </h5>
                  <p className="iran-sans text-muted">
                    {hasActiveFilters
                      ? "با فیلترهای انتخابی، کاربری یافت نشد"
                      : "هنوز کاربری ثبت نشده است"}
                  </p>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            <div className="row mt-4 d-none d-md-block">
              <div className="col-12">
                <div className="card border-0 shadow-sm">
                  <div className="table-responsive">
                    <table className="table table-hover mb-0 iran-sans">
                      <thead
                        style={{
                          background:
                            "linear-gradient(135deg, #053251 0%, #2a5a72 100%)",
                          color: "white",
                        }}
                      >
                        <tr>
                          <th className="p-3">نام و نام خانوادگی</th>
                          <th className="p-3">شماره موبایل</th>
                          <th className="p-3">کد ملی</th>
                          <th className="p-3">ایمیل</th>
                          <th className="p-3">تاریخ ثبت‌نام</th>
                          <th className="p-3 text-center">عملیات</th>
                        </tr>
                      </thead>
                      <tbody>
                        {users.map((user) => (
                          <tr key={user.id}>
                            <td className="p-3 fw-bold">{user.fullName}</td>
                            <td className="p-3" dir="ltr">
                              {user.mobileNumber}
                            </td>
                            <td className="p-3" dir="ltr">
                              {user.nationalCode || "—"}
                            </td>
                            <td className="p-3">{user.email || "—"}</td>
                            <td className="p-3">{user.createdAtDisplay}</td>
                            <td className="p-3">
                              <div className="d-flex justify-content-center align-items-center gap-3">
                                <Tooltip title="ویرایش کاربر" arrow>
                                  <FontAwesomeIcon
                                    className="color-green"
                                    icon={faPenToSquare}
                                    size="lg"
                                    onClick={() =>
                                      navigate(`/edit-user/${user.id}`, {
                                        state: { from: location },
                                      })
                                    }
                                    style={{ cursor: "pointer" }}
                                  />
                                </Tooltip>

                                {CheckRole(["Admin"]) && (
                                  <Tooltip title="تغییر دسترسی" arrow>
                                    <FontAwesomeIcon
                                      className="color-blue"
                                      icon={faUserGear}
                                      size="lg"
                                      onClick={() => {
                                        setSelectedUser(user);
                                        setOpenEditModal(true);
                                      }}
                                      style={{ cursor: "pointer" }}
                                    />
                                  </Tooltip>
                                )}

                                <Tooltip title="حذف کاربر" arrow>
                                  <FontAwesomeIcon
                                    className="text-danger"
                                    icon={faTrash}
                                    size="lg"
                                    onClick={() => {
                                      setSelectedUserId(user.id);
                                      setOpenDialog(true);
                                    }}
                                    style={{ cursor: "pointer" }}
                                  />
                                </Tooltip>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>

            {/* Mobile Cards */}
            <div className="row mt-4 d-md-none">
              {users.map((user) => (
                <div key={user.id} className="col-12 mb-3">
                  <div
                    className="card border-0 shadow-sm"
                    style={{
                      borderRadius: "16px",
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        background:
                          "linear-gradient(135deg, #053251 0%, #2a5a72 100%)",
                        padding: "12px 16px",
                        color: "white",
                      }}
                    >
                      <div className="d-flex justify-content-between align-items-center">
                        <span className="iran-sans fw-bold">
                          {user.fullName}
                        </span>
                        <div className="d-flex gap-2">
                          <Tooltip title="ویرایش" arrow>
                            <FontAwesomeIcon
                              icon={faPenToSquare}
                              style={{
                                cursor: "pointer",
                                color: "#7cc890",
                              }}
                              onClick={() =>
                                navigate(`/edit-user/${user.id}`, {
                                  state: { from: location },
                                })
                              }
                            />
                          </Tooltip>

                          {CheckRole(["Admin"]) && (
                            <Tooltip title="دسترسی" arrow>
                              <FontAwesomeIcon
                                icon={faUserGear}
                                style={{
                                  cursor: "pointer",
                                  color: "white",
                                }}
                                onClick={() => {
                                  setSelectedUser(user);
                                  setOpenEditModal(true);
                                }}
                              />
                            </Tooltip>
                          )}

                          <Tooltip title="حذف" arrow>
                            <FontAwesomeIcon
                              icon={faTrash}
                              style={{
                                cursor: "pointer",
                                color: "#ff6b6b",
                              }}
                              onClick={() => {
                                setSelectedUserId(user.id);
                                setOpenDialog(true);
                              }}
                            />
                          </Tooltip>
                        </div>
                      </div>
                    </div>

                    <div className="card-body p-3">
                      <div className="iran-sans" style={{ fontSize: "14px" }}>
                        <div className="mb-2">
                          <strong>موبایل:</strong>
                          <span className="ms-2" dir="ltr">
                            {user.mobileNumber}
                          </span>
                        </div>
                        {user.nationalCode && (
                          <div className="mb-2">
                            <strong>کد ملی:</strong>
                            <span className="ms-2" dir="ltr">
                              {user.nationalCode}
                            </span>
                          </div>
                        )}
                        {user.email && (
                          <div className="mb-2">
                            <strong>ایمیل:</strong>
                            <span className="ms-2">{user.email}</span>
                          </div>
                        )}
                        <div>
                          <strong>تاریخ ثبت‌نام:</strong>
                          <span className="ms-2">{user.createdAtDisplay}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="row mt-4 pb-5">
                <div className="col-12 d-flex justify-content-center">
                  <Pagination
                    count={totalPages}
                    page={currentPage}
                    onChange={handlePageChange}
                    color="primary"
                    size="large"
                    showFirstButton
                    showLastButton
                    sx={{
                      "& .MuiPaginationItem-root": {
                        fontFamily: "MyPersianFont, sans-serif",
                        "&.Mui-selected": {
                          backgroundColor: "#7cc890",
                          color: "white",
                          "&:hover": {
                            backgroundColor: "#6bb57f",
                          },
                        },
                      },
                    }}
                  />
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <ConfirmModal
        handleCloseDialog={() => setOpenDialog(false)}
        handleConfirm={deleteUserHandler}
        openDialog={openDialog}
        text={["آیا از حذف این کاربر مطمئن هستید؟"]}
        cancleText="خیر"
        confirmText="بله"
        title="حذف کاربر"
      />

      <EditModal
        open={openEditModal}
        handleClose={() => setOpenEditModal(false)}
        user={selectedUser}
      />

      <Notification
        openSnackbar={openSnackbar}
        snackbarMessage={snackbarMessage}
        snackbarSeverity={snackbarSeverity}
        setOpenSnackbar={setOpenSnackbar}
      />
    </Content>
  );
};

export default UserIndex;
